import { useState, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ChartGantt, Layers, Workflow } from 'lucide-react'
import { useProject } from './ProjectLayout'
import { StoryDrawer } from './StoryDrawer'
import { Card, Chip, Tabs, cn } from '../../design'
import { listStories, type Story } from '../../api/stories'
import { type EpicLookup } from '../../api/users'
import { api } from '../../api/client'

type Epic = EpicLookup & { start_date?: string | null; due_date?: string | null }

/**
 * Phase 9 — Timeline V1+V2+V3 (per `wf-timeline-team.jsx` Timeline section).
 *  V1 Gantt: epics + nested story bars + today line + dep arrows
 *  V2 Lanes: chunky lane per epic with milestone diamond at due_date
 *  V3 Graph: SVG dependency graph; nodes = stories with deps; edges = blocks/relates
 */
export default function Timeline() {
  const project = useProject()
  const [params, setParams] = useSearchParams()
  const storyKey = params.get('story')
  const [drawerOpen, setDrawerOpen] = useState(false)

  function openStory(key: string) {
    const next = new URLSearchParams(params)
    next.set('story', key)
    setParams(next, { replace: true })
    setDrawerOpen(true)
  }
  function closeStory() {
    setDrawerOpen(false)
    const next = new URLSearchParams(params)
    next.delete('story')
    setParams(next, { replace: true })
  }
  // Auto-open if URL has ?story=
  useMemo(() => {
    if (storyKey && !drawerOpen) setDrawerOpen(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storyKey])

  return (
    <div className="flex flex-col h-full">
      <div className="px-6 py-4 border-b border-border bg-bg-elevated">
        <h2 className="text-base font-semibold tracking-tight">Timeline</h2>
        <p className="text-xs text-fg-subtle">
          Three lenses on schedule. Gantt bars are the plan (planned start → due); the thin line under a story is what actually happened (dashed while still open).
        </p>
      </div>

      <div className="flex-1 min-h-0 overflow-auto">
        <div className="mx-auto max-w-7xl px-6 py-6">
          <Tabs
            items={[
              { value: 'gantt',  label: <TabLabel icon={<ChartGantt size={13} />}>Gantt</TabLabel>,
                content: <GanttView projectId={project.id} onOpenStory={openStory} /> },
              { value: 'lanes',  label: <TabLabel icon={<Layers size={13} />}>Lanes</TabLabel>,
                content: <LaneStack projectId={project.id} /> },
              { value: 'graph',  label: <TabLabel icon={<Workflow size={13} />}>Graph</TabLabel>,
                content: <DependencyGraph projectId={project.id} onOpenStory={openStory} /> },
            ]}
            defaultValue="gantt"
          />
        </div>
      </div>

      <StoryDrawer
        storyKey={storyKey}
        projectId={project.id}
        open={drawerOpen}
        onOpenChange={(o) => { if (!o) closeStory(); else setDrawerOpen(true) }}
      />
    </div>
  )
}

function TabLabel({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return <span className="inline-flex items-center gap-1.5">{icon}{children}</span>
}

// ─── Common data ─────────────────────────────────────────────────────────

function useTimelineData(projectId: number) {
  // /epics returns full rows (start_date, due_date, etc.) — single fetch.
  // (Lookup endpoint dropped — was a duplicate of /epics for this view.)
  const epicsQ = useQuery({
    queryKey: ['epics', projectId],
    queryFn: () => api<Array<EpicLookup & { start_date?: string | null; due_date?: string | null; story_count?: number; done_count?: number }>>(
      '/epics', { query: { project_id: projectId } },
    ),
    staleTime: 30_000,
  })
  const storiesQ = useQuery({
    queryKey: ['stories', 'timeline', projectId],
    queryFn: () => listStories({ project_id: projectId, per_page: 500 }),
    refetchOnWindowFocus: true,
    staleTime: 15_000,
  })

  const epics = (epicsQ.data ?? []) as Array<EpicLookup & {
    start_date?: string | null
    due_date?: string | null
  }>
  const stories = storiesQ.data?.data ?? []
  const isLoading  = epicsQ.isLoading  || storiesQ.isLoading
  const isFetching = epicsQ.isFetching || storiesQ.isFetching

  return { epics, stories, isLoading, isFetching }
}

// ─── V1: Gantt ────────────────────────────────────────────────────────────

const ROW_HEIGHT = 32
const LABEL_WIDTH = 220

function GanttView({ projectId, onOpenStory }: { projectId: number; onOpenStory: (key: string) => void }) {
  const { epics, stories, isLoading, isFetching } = useTimelineData(projectId)

  // Range: from earliest of (scheduled_start | created_at) to latest of (scheduled_end | due_date | scheduled_start + 1w)
  const range = useMemo(() => computeRange(stories, epics), [stories, epics])
  const weeks = useMemo(() => weeksInRange(range.start, range.end), [range])

  // Build rows: each epic followed by its stories
  const rows = useMemo(() => {
    const r: Array<
      | { kind: 'epic'; epic: Epic; start: Date; end: Date }
      | { kind: 'story'; story: Story; start: Date; end: Date }
    > = []
    for (const e of epics) {
      const epicStories = stories.filter(s => s.epic_id === e.id)
      const epicStart = e.start_date ? new Date(e.start_date + 'T00:00:00')
        : minDate(epicStories.map(s => storyStart(s))) ?? range.start
      const epicEnd = e.due_date ? new Date(e.due_date + 'T00:00:00')
        : maxDate(epicStories.map(s => storyEnd(s))) ?? new Date(epicStart.getTime() + 7 * 86_400_000)
      r.push({ kind: 'epic', epic: e, start: epicStart, end: epicEnd })
      for (const s of epicStories) {
        r.push({ kind: 'story', story: s, start: storyStart(s), end: storyEnd(s) })
      }
    }
    // Stories without an epic
    const noEpic = stories.filter(s => s.epic_id === null)
    for (const s of noEpic) {
      r.push({ kind: 'story', story: s, start: storyStart(s), end: storyEnd(s) })
    }
    return r
  }, [epics, stories, range])

  if (isLoading) return <CenterSpinner />
  if (rows.length === 0) {
    return <p className="mt-6 text-sm text-fg-subtle text-center">No epics or stories yet.</p>
  }

  const totalWeeks = weeks.length
  const today = new Date()
  const todayPct = (today.getTime() - range.start.getTime()) / Math.max(1, range.end.getTime() - range.start.getTime())

  return (
    <div className="mt-4 relative">
      {isFetching && (
        <div className="absolute top-2 right-3 z-30 flex items-center gap-1.5 text-[10px] text-fg-subtle bg-bg-elevated border border-border rounded-full px-2 py-1 shadow-sm">
          <span className="h-2 w-2 rounded-full border border-accent border-t-transparent animate-spin" />
          syncing
        </div>
      )}
      <Card padded={false}>
        <div className="overflow-x-auto">
          <div style={{ minWidth: LABEL_WIDTH + totalWeeks * 60 }}>
            {/* Week header */}
            <div
              className="grid border-b border-border bg-bg-muted/50 sticky top-0 z-10"
              style={{ gridTemplateColumns: `${LABEL_WIDTH}px repeat(${totalWeeks}, minmax(60px, 1fr))` }}
            >
              <div className="px-3 py-2 text-xs font-semibold text-fg-muted">Item</div>
              {weeks.map((w, i) => (
                <div
                  key={i}
                  className={cn(
                    'px-2 py-2 text-[10px] font-mono text-center border-l border-border',
                    isCurrentWeek(w) && 'text-accent font-semibold',
                  )}
                >
                  {fmtWeek(w)}
                </div>
              ))}
            </div>

            {/* Rows */}
            <div className="relative">
              {rows.map((row, i) => (
                <div
                  key={i}
                  className="grid border-b border-border last:border-b-0 hover:bg-bg-muted/30"
                  style={{
                    gridTemplateColumns: `${LABEL_WIDTH}px repeat(${totalWeeks}, minmax(60px, 1fr))`,
                    height: ROW_HEIGHT,
                  }}
                >
                  <div
                    className={cn(
                      'px-3 flex items-center gap-2 truncate',
                      row.kind === 'epic'
                        ? 'font-semibold text-sm'
                        : 'pl-8 text-xs text-fg-muted',
                    )}
                  >
                    {row.kind === 'epic' ? (
                      <>
                        <span
                          className="inline-block h-2 w-2 rounded-full"
                          style={{ backgroundColor: row.epic.color ?? '#9aa0a6' }}
                        />
                        <span className="truncate">{row.epic.name}</span>
                      </>
                    ) : (
                      <>
                        <span className="font-mono text-[10px] text-fg-subtle">{row.story.key}</span>
                        <span className="truncate">{row.story.title}</span>
                      </>
                    )}
                  </div>
                  {/* Bar */}
                  <Bar
                    range={range}
                    weeks={totalWeeks}
                    start={row.start}
                    end={row.end}
                    color={row.kind === 'epic' ? (row.epic.color ?? '#9aa0a6') : (row.story.epic_color ?? '#9aa0a6')}
                    isEpic={row.kind === 'epic'}
                    actual={row.kind === 'story' ? storyActual(row.story) : null}
                    onClick={row.kind === 'story' ? () => onOpenStory(row.story.key) : undefined}
                    label={row.kind === 'epic' ? row.epic.name : row.story.title}
                  />
                </div>
              ))}

              {/* Today vertical line */}
              {todayPct >= 0 && todayPct <= 1 && (
                <div
                  className="absolute top-0 bottom-0 w-px bg-accent pointer-events-none"
                  style={{ left: `calc(${LABEL_WIDTH}px + (100% - ${LABEL_WIDTH}px) * ${todayPct})` }}
                >
                  <div className="absolute -top-5 -left-5 text-[10px] text-accent font-semibold">today</div>
                </div>
              )}

              {/* Dependency arrows (overlay SVG) */}
              <DepArrows
                rows={rows}
                stories={stories}
                range={range}
                projectId={projectId}
              />
            </div>
          </div>
        </div>
      </Card>
    </div>
  )
}

function Bar({
  range,
  weeks,
  start,
  end,
  color,
  isEpic,
  actual,
  onClick,
  label,
}: {
  range: { start: Date; end: Date }
  weeks: number
  start: Date
  end: Date
  color: string
  isEpic: boolean
  /** Story rows: when work really ran, drawn as a thin bar under the plan. */
  actual?: { start: Date; end: Date; done: boolean } | null
  onClick?: () => void
  label: string
}) {
  const total = range.end.getTime() - range.start.getTime()
  const left = ((start.getTime() - range.start.getTime()) / total) * 100
  const width = Math.max(2, ((end.getTime() - start.getTime()) / total) * 100)
  return (
    <div
      className="relative col-span-full"
      style={{ gridColumn: `2 / span ${weeks}` }}
    >
      <button
        type="button"
        onClick={onClick}
        title={`${label} · planned ${start.toLocaleDateString()} → ${end.toLocaleDateString()}${actual ? ` · actual ${actual.start.toLocaleDateString()} → ${actual.done ? actual.end.toLocaleDateString() : 'in progress'}` : ''}`}
        className={cn(
          'absolute top-1/2 -translate-y-1/2 rounded-md text-[10px] font-medium overflow-hidden text-left px-2 truncate',
          isEpic ? 'h-5 border-2' : 'h-3.5 border',
          onClick && 'cursor-pointer hover:ring-2 hover:ring-accent/50',
          !onClick && 'cursor-default',
        )}
        style={{
          left: `${left}%`,
          width: `${width}%`,
          backgroundColor: isEpic ? `${color}30` : `${color}55`,
          borderColor: color,
          color: isEpic ? color : '#1a1a1a',
        }}
        disabled={!onClick}
      >
        {isEpic && '━━━'}
      </button>
      {actual && (
        <span
          aria-hidden
          title={`Actual: ${actual.start.toLocaleDateString()} → ${actual.done ? actual.end.toLocaleDateString() : 'in progress'}`}
          className="absolute rounded-full pointer-events-none"
          style={{
            top: 'calc(50% + 9px)',
            height: 3,
            left: `${((actual.start.getTime() - range.start.getTime()) / total) * 100}%`,
            width: `${Math.max(0.6, ((actual.end.getTime() - actual.start.getTime()) / total) * 100)}%`,
            background: actual.done ? '#1a1a1a' : `repeating-linear-gradient(90deg, #1a1a1a 0 4px, transparent 4px 7px)`,
            opacity: 0.7,
          }}
        />
      )}
    </div>
  )
}

type Row =
  | { kind: 'epic'; epic: Epic; start: Date; end: Date }
  | { kind: 'story'; story: Story; start: Date; end: Date }

function DepArrows({
  rows,
  stories,
  range,
  projectId,
}: {
  rows: Row[]
  stories: Story[]
  range: { start: Date; end: Date }
  projectId: number
}) {
  // Bulk fetch all project dependencies in one round-trip (was N+1 per story)
  const depsQ = useQuery({
    queryKey: ['dependencies', 'project', projectId],
    queryFn: () => api<Array<{ id: number; from_story_id: number; to_story_id: number; kind: string }>>(
      '/dependencies', { query: { project_id: projectId } },
    ),
    enabled: stories.length > 0,
    staleTime: 30_000,
  })

  const total = range.end.getTime() - range.start.getTime()
  const rowIndex = (storyId: number) =>
    rows.findIndex(r => r.kind === 'story' && r.story.id === storyId)

  const arrows = (depsQ.data ?? []).map((d, i) => {
    const fr = rowIndex(d.from_story_id)
    const to = rowIndex(d.to_story_id)
    if (fr < 0 || to < 0) return null
    const fromStory = stories.find(s => s.id === d.from_story_id)!
    const toStory   = stories.find(s => s.id === d.to_story_id)!
    const fromX = ((storyEnd(fromStory).getTime() - range.start.getTime()) / total)
    const toX   = ((storyStart(toStory).getTime() - range.start.getTime()) / total)
    return { i, fr, to, fromX, toX, kind: d.kind }
  }).filter((x): x is NonNullable<typeof x> => !!x)

  if (arrows.length === 0) return null

  const totalRows = rows.length

  return (
    <svg
      className="absolute pointer-events-none"
      style={{
        top: 0,
        left: LABEL_WIDTH,
        width: `calc(100% - ${LABEL_WIDTH}px)`,
        height: totalRows * ROW_HEIGHT,
      }}
    >
      <defs>
        <marker id="arrowhead" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto">
          <path d="M0,0 L10,5 L0,10 z" fill="var(--color-danger)" />
        </marker>
      </defs>
      {arrows.map(a => {
        const y1 = a.fr * ROW_HEIGHT + ROW_HEIGHT / 2
        const y2 = a.to * ROW_HEIGHT + ROW_HEIGHT / 2
        const x1 = `${a.fromX * 100}%`
        const x2 = `${a.toX * 100}%`
        return (
          <path
            key={a.i}
            d={`M ${x1} ${y1} C calc(${x1} + 30px) ${y1}, calc(${x2} - 30px) ${y2}, ${x2} ${y2}`}
            fill="none"
            stroke="var(--color-danger)"
            strokeWidth="1.5"
            strokeDasharray={a.kind === 'blocks' ? undefined : '4,3'}
            markerEnd="url(#arrowhead)"
            opacity={0.6}
          />
        )
      })}
    </svg>
  )
}

// ─── V2: Lane Stack ──────────────────────────────────────────────────────

function LaneStack({ projectId }: { projectId: number }) {
  const { epics, stories, isLoading, isFetching } = useTimelineData(projectId)

  const range = useMemo(() => computeRange(stories, epics), [stories, epics])
  const months = useMemo(() => monthsInRange(range.start, range.end), [range])

  if (isLoading) return <CenterSpinner />

  return (
    <div className="mt-4 relative">
      {isFetching && (
        <div className="absolute top-2 right-3 z-30 flex items-center gap-1.5 text-[10px] text-fg-subtle bg-bg-elevated border border-border rounded-full px-2 py-1 shadow-sm">
          <span className="h-2 w-2 rounded-full border border-accent border-t-transparent animate-spin" />
          syncing
        </div>
      )}
      <Card padded>
        <div className="grid mb-4" style={{ gridTemplateColumns: `120px 1fr` }}>
          <div />
          <div className="grid" style={{ gridTemplateColumns: `repeat(${months.length}, 1fr)` }}>
            {months.map((m, i) => (
              <div
                key={i}
                className={cn(
                  'text-center text-sm font-semibold tracking-tight',
                  isCurrentMonth(m) ? 'text-accent' : 'text-fg-muted',
                )}
              >
                {m.toLocaleDateString(undefined, { month: 'short', year: '2-digit' })}
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          {epics.map(e => {
            const epicStories = stories.filter(s => s.epic_id === e.id)
            const epicStart = e.start_date ? new Date(e.start_date + 'T00:00:00')
              : minDate(epicStories.map(s => storyStart(s))) ?? range.start
            const epicEnd = e.due_date ? new Date(e.due_date + 'T00:00:00')
              : maxDate(epicStories.map(s => storyEnd(s))) ?? new Date(epicStart.getTime() + 14 * 86_400_000)
            const total = range.end.getTime() - range.start.getTime()
            const left = ((epicStart.getTime() - range.start.getTime()) / total) * 100
            const width = ((epicEnd.getTime() - epicStart.getTime()) / total) * 100
            const milestoneLeft = ((epicEnd.getTime() - range.start.getTime()) / total) * 100

            return (
              <div key={e.id} className="grid items-center gap-3" style={{ gridTemplateColumns: `120px 1fr` }}>
                <div className="text-sm font-semibold tracking-tight truncate">{e.name}</div>
                <div className="relative h-12 rounded-md bg-bg-muted/30 border border-border">
                  <div className="absolute inset-x-0 top-1/2 border-t border-dashed border-border" />
                  <div
                    className="absolute top-2 bottom-2 rounded-md border-2 flex items-center px-3 text-xs font-medium tracking-tight shadow-sm"
                    style={{
                      left: `${left}%`,
                      width: `${Math.max(2, width)}%`,
                      backgroundColor: `${e.color ?? '#9aa0a6'}20`,
                      borderColor: e.color ?? '#9aa0a6',
                      color: e.color ?? '#1a1a1a',
                    }}
                    title={`${e.name} · ${epicStart.toLocaleDateString()} → ${epicEnd.toLocaleDateString()}`}
                  >
                    <span className="truncate">{epicStories.length} stories</span>
                  </div>
                  {/* Milestone diamond at end */}
                  <div
                    className="absolute"
                    style={{
                      left: `${milestoneLeft}%`,
                      top: '50%',
                      transform: 'translate(-50%, -50%) rotate(45deg)',
                      width: 16,
                      height: 16,
                      backgroundColor: e.color ?? '#1a1a1a',
                      boxShadow: '1px 2px 0 rgba(0,0,0,0.1)',
                    }}
                    title={`${e.name} milestone · ${epicEnd.toLocaleDateString()}`}
                  />
                  <div
                    className="absolute text-[10px] font-mono whitespace-nowrap text-fg-subtle"
                    style={{ left: `${milestoneLeft}%`, top: '100%', transform: 'translateX(-50%)' }}
                  >
                    ◆ {fmtMonthDay(epicEnd)}
                  </div>
                </div>
              </div>
            )
          })}
          {epics.length === 0 && (
            <p className="text-sm text-fg-subtle text-center py-6">No epics yet.</p>
          )}
        </div>
      </Card>
    </div>
  )
}

// ─── V3: Dependency Graph ────────────────────────────────────────────────

function DependencyGraph({ projectId, onOpenStory }: { projectId: number; onOpenStory: (key: string) => void }) {
  const { stories, isLoading } = useTimelineData(projectId)

  // Bulk fetch all project dependencies in one round-trip
  const depsQ = useQuery({
    queryKey: ['dependencies', 'project', projectId],
    queryFn: () => api<Array<{ id: number; from_story_id: number; to_story_id: number; kind: string }>>(
      '/dependencies', { query: { project_id: projectId } },
    ),
    enabled: stories.length > 0,
    staleTime: 30_000,
  })

  // Layout: levels by topological-ish ordering
  const layout = useMemo(() => {
    const raw = depsQ.data ?? []
    const deps = raw.map(d => ({ from_id: d.from_story_id, to_id: d.to_story_id, kind: d.kind }))
    const idsWithDeps = new Set<number>()
    for (const d of deps) { idsWithDeps.add(d.from_id); idsWithDeps.add(d.to_id) }
    const nodes = stories.filter(s => idsWithDeps.has(s.id))
    if (nodes.length === 0) return { positions: new Map<number, { x: number; y: number }>(), nodes: [] as Story[], edges: deps }

    // Compute level: longest path from any source
    const incoming: Record<number, number[]> = {}
    for (const d of deps) (incoming[d.to_id] ??= []).push(d.from_id)
    const level: Record<number, number> = {}
    function lvl(id: number, seen: Set<number> = new Set()): number {
      if (seen.has(id)) return 0
      seen.add(id)
      if (level[id] !== undefined) return level[id]!
      const inc = incoming[id] ?? []
      const v = inc.length === 0 ? 0 : 1 + Math.max(...inc.map(p => lvl(p, seen)))
      level[id] = v
      return v
    }
    nodes.forEach(n => lvl(n.id))

    const byLevel: Record<number, Story[]> = {}
    for (const n of nodes) (byLevel[level[n.id] ?? 0] ??= []).push(n)
    const maxLvl = Math.max(0, ...Object.keys(byLevel).map(Number))
    const positions = new Map<number, { x: number; y: number }>()
    const COL_W = 220, ROW_H = 100, MARGIN = 30
    for (let l = 0; l <= maxLvl; l++) {
      const col = byLevel[l] ?? []
      col.forEach((n, idx) => {
        positions.set(n.id, { x: MARGIN + l * COL_W, y: MARGIN + idx * ROW_H })
      })
    }
    return { positions, nodes, edges: deps }
  }, [stories, depsQ.data])

  if (isLoading) return <CenterSpinner />

  if (layout.nodes.length === 0) {
    return (
      <Card className="mt-4">
        <h3 className="text-sm font-semibold mb-1">No dependencies yet</h3>
        <p className="text-xs text-fg-subtle">
          Add a "blocks" or "relates" link from the story drawer to see the graph here.
        </p>
      </Card>
    )
  }

  // SVG canvas size
  const maxX = Math.max(...Array.from(layout.positions.values()).map(p => p.x)) + 200
  const maxY = Math.max(...Array.from(layout.positions.values()).map(p => p.y)) + 80

  return (
    <Card padded={false} className="mt-4 overflow-hidden">
      <div className="relative" style={{ width: '100%', height: maxY + 40 }}>
        <svg
          className="absolute inset-0"
          viewBox={`0 0 ${Math.max(maxX, 800)} ${maxY + 40}`}
          preserveAspectRatio="xMinYMin meet"
          style={{ width: '100%', height: '100%' }}
        >
          <defs>
            <marker id="depArrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto">
              <path d="M0,0 L10,5 L0,10 z" fill="var(--color-danger)" />
            </marker>
            <marker id="depArrowMuted" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto">
              <path d="M0,0 L10,5 L0,10 z" fill="var(--color-fg-subtle)" />
            </marker>
          </defs>
          {layout.edges.map((d, i) => {
            const fp = layout.positions.get(d.from_id)
            const tp = layout.positions.get(d.to_id)
            if (!fp || !tp) return null
            const x1 = fp.x + 180, y1 = fp.y + 30
            const x2 = tp.x,       y2 = tp.y + 30
            const cx = (x1 + x2) / 2
            const isBlock = d.kind === 'blocks'
            return (
              <path
                key={i}
                d={`M ${x1} ${y1} C ${cx} ${y1}, ${cx} ${y2}, ${x2} ${y2}`}
                fill="none"
                stroke={isBlock ? 'var(--color-danger)' : 'var(--color-fg-subtle)'}
                strokeWidth={isBlock ? 2 : 1.5}
                strokeDasharray={isBlock ? undefined : '5,3'}
                markerEnd={isBlock ? 'url(#depArrow)' : 'url(#depArrowMuted)'}
                opacity={isBlock ? 0.85 : 0.5}
              />
            )
          })}
        </svg>
        {layout.nodes.map(n => {
          const p = layout.positions.get(n.id)!
          return (
            <button
              key={n.id}
              onClick={() => onOpenStory(n.key)}
              className="absolute rounded-lg border-2 p-2.5 shadow-sm bg-bg-elevated hover:shadow-md transition-shadow text-left w-[180px]"
              style={{
                left: p.x,
                top: p.y,
                borderColor: n.epic_color ?? '#9aa0a6',
              }}
            >
              <div className="flex items-center gap-1.5 mb-1">
                <span
                  className="inline-block h-1.5 w-1.5 rounded-full"
                  style={{ backgroundColor: n.epic_color ?? '#9aa0a6' }}
                />
                <span className="font-mono text-[10px] text-fg-subtle">{n.key}</span>
                <Chip
                  size="sm"
                  tone={
                    n.status_category === 'done' ? 'success'
                    : n.status_category === 'in_progress' ? 'info'
                    : n.status_category === 'in_review' ? 'warning'
                    : 'neutral'
                  }
                  className="ml-auto"
                >
                  {n.status_name}
                </Chip>
              </div>
              <div className="text-xs leading-snug line-clamp-2">{n.title}</div>
            </button>
          )
        })}
      </div>
      <div className="px-4 py-3 border-t border-border bg-bg-muted/30 flex items-center gap-4 text-[11px] text-fg-subtle">
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-4 h-px bg-danger" /> blocks
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-4 h-px border-t border-dashed border-fg-subtle" /> relates / duplicates
        </span>
        <span className="ml-auto font-mono">
          {layout.nodes.length} nodes · {layout.edges.length} edges
        </span>
      </div>
    </Card>
  )
}

// ─── helpers ─────────────────────────────────────────────────────────────

function CenterSpinner() {
  return (
    <div className="flex items-center justify-center h-40">
      <div className="h-6 w-6 rounded-full border-2 border-border border-t-accent animate-spin" />
    </div>
  )
}

/**
 * The plan bar: planned_start → due_date. Stories without a planned start
 * fall back to their calendar block, then to when they were created.
 */
function storyStart(s: Story): Date {
  if (s.planned_start) return new Date(s.planned_start + 'T00:00:00')
  if (s.scheduled_start) return new Date(s.scheduled_start.replace(' ', 'T'))
  return new Date(s.created_at.replace(' ', 'T'))
}
function storyEnd(s: Story): Date {
  if (s.due_date) return new Date(s.due_date + 'T23:59:59')
  if (s.scheduled_end) return new Date(s.scheduled_end.replace(' ', 'T'))
  // Fall back to start + estimated days based on points (1pt = 1 day, fallback 2 days)
  const days = (s.estimate_points ?? 2)
  return new Date(storyStart(s).getTime() + days * 86_400_000)
}
/** The actual bar: actual_start → completed_at, or → today while still open. */
function storyActual(s: Story): { start: Date; end: Date; done: boolean } | null {
  if (!s.actual_start) return null
  const start = new Date(s.actual_start + 'T00:00:00')
  const done = !!s.completed_at
  const end = done ? new Date(s.completed_at!.replace(' ', 'T')) : new Date()
  return { start, end: end > start ? end : new Date(start.getTime() + 86_400_000), done }
}
function minDate(ds: Date[]): Date | null {
  if (ds.length === 0) return null
  return ds.reduce((a, b) => (a < b ? a : b))
}
function maxDate(ds: Date[]): Date | null {
  if (ds.length === 0) return null
  return ds.reduce((a, b) => (a > b ? a : b))
}
function computeRange(stories: Story[], epics: Epic[]): { start: Date; end: Date } {
  const dates: Date[] = []
  for (const s of stories) {
    dates.push(storyStart(s)); dates.push(storyEnd(s))
    const a = storyActual(s)
    if (a) { dates.push(a.start); dates.push(a.end) }
  }
  for (const e of epics) {
    if (e.start_date) dates.push(new Date(e.start_date + 'T00:00:00'))
    if (e.due_date)   dates.push(new Date(e.due_date   + 'T00:00:00'))
  }
  if (dates.length === 0) {
    const today = new Date()
    return { start: today, end: new Date(today.getTime() + 30 * 86_400_000) }
  }
  const min = minDate(dates)!
  const max = maxDate(dates)!
  // Pad: round start down to Sunday, end up to Sunday
  const start = new Date(min); start.setHours(0, 0, 0, 0); start.setDate(start.getDate() - start.getDay())
  const end   = new Date(max); end.setHours(0, 0, 0, 0); end.setDate(end.getDate() + (7 - end.getDay()))
  // Min span 4 weeks
  if (end.getTime() - start.getTime() < 28 * 86_400_000) {
    end.setTime(start.getTime() + 28 * 86_400_000)
  }
  return { start, end }
}
function weeksInRange(start: Date, end: Date): Date[] {
  const out: Date[] = []
  for (let d = new Date(start); d < end; d.setDate(d.getDate() + 7)) {
    out.push(new Date(d))
  }
  return out
}
function monthsInRange(start: Date, end: Date): Date[] {
  const out: Date[] = []
  const c = new Date(start.getFullYear(), start.getMonth(), 1)
  while (c <= end) {
    out.push(new Date(c))
    c.setMonth(c.getMonth() + 1)
  }
  return out
}
function isCurrentWeek(weekStart: Date): boolean {
  const today = new Date()
  return today >= weekStart && today.getTime() < weekStart.getTime() + 7 * 86_400_000
}
function isCurrentMonth(m: Date): boolean {
  const today = new Date()
  return today.getFullYear() === m.getFullYear() && today.getMonth() === m.getMonth()
}
function fmtWeek(d: Date): string {
  return `${d.getMonth() + 1}/${d.getDate()}`
}
function fmtMonthDay(d: Date): string {
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}
